import {
  ALERT_TYPE,
  FEATURES,
  IAlertData,
  ICounterData,
  Unsubscribe,
} from "@home-health-notify/domain";
import { OrganizationContext, useTranslation } from "hhn-commons";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  ChatListTabItem,
  MenuBarTabsContainer,
  MessagePanelBox,
  MessagePanelContainer,
  MessagePanelMenuBar,
} from "../chats/ChatListPanel.style";
import TabPanel from "../common/tabs/TabPanel";
import { ROUTES } from "../routes";
import AcknowledgementAlert from "./acknowledgementAlert/AcknowledgementAlert";
import {
  AcknowledgementIcon,
  AlertTabBadge,
  PriorityIcon,
  ReferralIcon,
  StarredMessageIcon,
} from "./Alert.style";
import PriorityAlert from "./priorityAlert/PriorityAlert";
import ReferralAlert from "./referralAlert/ReferralAlert";
import StarredMessages from "./starredMessage/StarredMessages";

const Alert = ({ counters }: { counters: ICounterData }) => {
  const { t } = useTranslation();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const tNs = (key: string) => t(key, { ns: "chat" });
  const canViewReferralAlerts = !!selectedOrgUser?.hasAccess(
    FEATURES.VIEW_REFERRAL_ALERTS,
  );
  const allTabs = [
    {
      index: 0,
      icon: <PriorityIcon />,
      label: tNs("alerts.priority"),
      path: ROUTES.PATIENT,
      invisible: counters.alert[ALERT_TYPE.PRIORITY] <= 0,
      access: true,
    },
    {
      index: 1,
      icon: <AcknowledgementIcon />,
      label: tNs("alerts.acknowledge"),
      path: "",
      invisible: counters.alert[ALERT_TYPE.ACKNOWLEDGE] <= 0,
      access: true,
    },
    {
      index: 2,
      icon: <ReferralIcon />,
      label: tNs("alerts.referral"),
      path: "",
      invisible: counters.alert[ALERT_TYPE.REFERRAL] <= 0,
      access: canViewReferralAlerts,
    },
    {
      index: 3,
      icon: <StarredMessageIcon />,
      label: tNs("alerts.starred"),
      path: "",
      invisible: true,
      access: true,
    },
  ];
  const params = useParams();
  const pathArr = params["*"]?.split("/");
  const currentPath = pathArr && pathArr[0];
  const currentTab = currentPath
    ? allTabs.find(tab => currentPath === tab.path)
    : undefined;
  const activeTab = currentTab ? currentTab.index : allTabs[0].index;
  const [tabValue, setTabValue] = useState(activeTab);
  const [alerts, setAlerts] = useState<IAlertData>(
    selectedOrgUser?.alert.getAlerts() || {
      priority: { data: [], hasNext: true, limit: 10, total: 0 },
      acknowledgement: { data: [], hasNext: true, limit: 10, total: 0 },
      referral: { data: [], hasNext: true, limit: 10, total: 0 },
      sentAcknowledgement: { data: [], hasNext: true, limit: 10, total: 0 },
    },
  );

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const removeListeners: Unsubscribe[] = [];
    if (selectedOrgUser?.alert) {
      const removeRegisterChangeListener =
        selectedOrgUser.alert.registerChangeListener(alerts => {
          setAlerts({ ...alerts });
        });

      removeListeners.push(removeRegisterChangeListener);
    }

    return () => {
      removeListeners.forEach(removeListener => removeListener());
    };
  }, [selectedOrgUser?.alert]);

  return (
    <MessagePanelContainer>
      <MessagePanelBox>
        <MessagePanelMenuBar>
          <MenuBarTabsContainer
            slotProps={{
              indicator: { style: { display: "none" } },
            }}
            value={tabValue}
            onChange={handleChange}
            aria-label="tabs in left panel"
            variant="fullWidth"
          >
            {allTabs
              .filter(tab => tab.access)
              .map(tab => (
                <ChatListTabItem
                  data-testid={tab.label}
                  key={"sub-tab" + tab.index}
                  icon={
                    <AlertTabBadge
                      variant="dot"
                      color="primary"
                      invisible={tab.invisible}
                      data-testid="alert-badge"
                    >
                      {tab.icon}
                    </AlertTabBadge>
                  }
                  label={tab.label}
                  value={tab.index}
                />
              ))}
          </MenuBarTabsContainer>
        </MessagePanelMenuBar>
      </MessagePanelBox>
      <TabPanel value={tabValue} index={0} uniqueKey="alert-list">
        <PriorityAlert
          counter={counters.alert[ALERT_TYPE.PRIORITY]}
          alerts={alerts.priority}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1} uniqueKey="alert-list">
        <AcknowledgementAlert
          counter={counters.alert[ALERT_TYPE.ACKNOWLEDGE]}
          alerts={alerts.acknowledgement}
        />
      </TabPanel>
      {canViewReferralAlerts && (
        <TabPanel value={tabValue} index={2} uniqueKey="alert-list">
          <ReferralAlert
            alerts={alerts.referral}
            counter={counters.alert[ALERT_TYPE.REFERRAL]}
          />
        </TabPanel>
      )}
      <TabPanel value={tabValue} index={3} uniqueKey="alert-list">
        <StarredMessages />
      </TabPanel>
    </MessagePanelContainer>
  );
};

export default Alert;
